import { Input, Select } from "antd";
import { InputProps } from "antd/es/input";
import { tenantAtom } from "atoms/tenantAtom.ts";
import classNames from "classnames";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { PayoutFormulaWrapper } from "components/UI/Table/UI/InteractiveCell/components/PayoutFormulaWrapper.tsx";
import { WithSaveButton } from "components/UI/Table/UI/InteractiveCell/components/WithSaveButton.tsx";
import { useInteractiveCell } from "components/UI/Table/UI/InteractiveCell/hooks/useInteractiveCell.ts";
import { FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import { useCurrencies } from "hooks/useCurrencies.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useMemo, useState, type MouseEvent } from "react";
import { FieldValues } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { useRecoilValue } from "recoil";
import {
    createFormulaParameterCategory,
    PAYOUT_FORMULA_PLACEHOLDER,
} from "utils/commission/formulaParameterCategories";
import { CurrencyCode, formatCurrency, formatCurrencyLabel } from "utils/formatCurrency.ts";

export interface ComposedValue {
    payoutFormula: string | undefined;
    formulaType: Exclude<SelectOptionValue, "disabled"> | undefined;
    isEnabled?: boolean;
}

type InputFieldProps<T extends FieldValues> = Omit<InputProps, "onChange" | "defaultValue"> & {
    defaultValue?: ComposedValue;
    onChange?: (value: ComposedValue) => void;
    currencyId?: number | null;
    isEnabled?: boolean;
} & Omit<FieldProps<T>, "onSave"> & {
        onSave?: (value: ComposedValue) => void;
    };

type AddonProps<T extends FieldValues> = { value?: SelectOptionValue } & FieldProps<T>;
type Props<T extends FieldValues> = InputFieldProps<T> & { addOnProps?: AddonProps<T> };

const SELECT_OPTIONS = [
    {
        label: "ƒ",
        value: "formula",
    },
    {
        label: "%",
        value: "percent",
    },
] as const;

const DISABLED_OPTION = {
    label: "🚫",
    value: "disabled",
} as const;

type SelectOptionValue = (typeof SELECT_OPTIONS)[number]["value"] | "fix" | "disabled";

const DEFAULT_ADD_ON_VALUE: SelectOptionValue = "formula";

export const WithPayoutFormula = <T extends FieldValues = FieldValues>({
    formControl,
    addOnProps,
    currencyId,
    ...props
}: Props<T>) => {
    const { onChange, value, className, defaultValue, isEnabled, onSave, ...restProps } = props;
    const { payoutFormula, formulaType, isEnabled: defaultIsEnabled } = defaultValue || {};

    // If isEnabled is explicitly false (either from props or defaultValue), use 'disabled'
    const shouldShowDisabled = isEnabled === false || defaultIsEnabled === false;
    const addOnInitValue = shouldShowDisabled ? "disabled" : formulaType || addOnProps?.value || DEFAULT_ADD_ON_VALUE;
    const initialValue = payoutFormula || value;

    const [addOnValue, setAddOnValue] = useState<SelectOptionValue>(addOnInitValue);

    // The collapsed cell's displayed value must reflect only what's actually persisted, not
    // whatever is being typed in the still-open editor — otherwise the table shows a value that
    // was never saved. Tracked separately from addOnValue/resolvedValue (which drive the live
    // editor inputs and do need to update on every keystroke), and only ever updated on save or
    // when fresh server data arrives via defaultValue below.
    const [savedComposedValue, setSavedComposedValue] = useState<{
        payoutFormula: string | number | undefined;
        addOnValue: SelectOptionValue | undefined;
    }>({ payoutFormula: initialValue as string | number | undefined, addOnValue: addOnInitValue });

    const { t: tF } = usePaldockTranslation("forms.manage-commission");
    const formulaCategories = useMemo(() => [createFormulaParameterCategory(tF)], [tF]);

    const {
        setControlledValue,
        handleOnSaveSideEffects,
        resolvedValue,
        isFormControlled,
        handleFocus,
        handleBlur,
        fieldRef,
        error,
    } = useInteractiveCell({
        formControl,
        value: initialValue,
    });

    // Sync internal state with defaultValue prop changes (only when not actively editing)
    useEffect(() => {
        // Don't sync if user is actively editing (focus/hover state would indicate this)
        // Only sync when defaultValue actually changes (new data from refetch)
        const {
            payoutFormula: newPayoutFormula,
            formulaType: newFormulaType,
            isEnabled: newIsEnabled,
        } = defaultValue || {};
        const newShouldShowDisabled = isEnabled === false || newIsEnabled === false;
        const newAddOnValue = newShouldShowDisabled
            ? "disabled"
            : newFormulaType || addOnProps?.value || DEFAULT_ADD_ON_VALUE;
        const newValue = newPayoutFormula || value;

        // Only update if the defaultValue prop itself changed (not just a re-render)
        // We check if the calculated values are different from current state
        const shouldUpdateAddOn = newAddOnValue !== addOnValue;
        const shouldUpdateValue = !isFormControlled && newValue !== resolvedValue && newValue !== undefined;

        if (shouldUpdateAddOn) {
            setAddOnValue(newAddOnValue);
        }

        if (shouldUpdateValue) {
            setControlledValue(newValue);
        }

        // Fresh server data — always trust it as the new saved/displayed value, regardless of
        // whether the live edit state above needed updating.
        setSavedComposedValue({
            payoutFormula: newValue as string | number | undefined,
            addOnValue: newAddOnValue,
        });
    }, [JSON.stringify(defaultValue), isEnabled, addOnProps?.value]);

    const handleChange = (value: ComposedValue) => {
        const { payoutFormula } = value;

        setControlledValue(payoutFormula);
        onChange?.(value);
    };

    const handleAddOnChange = (value: SelectOptionValue) => {
        // Amount holds a tag expression while type is "formula" — clear it when entering or leaving that type,
        // same as the create/edit commission form does.
        const crossingFormulaBoundary = (addOnValue === "formula") !== (value === "formula");

        setAddOnValue(value);
        handleChange({
            payoutFormula: crossingFormulaBoundary ? "" : (resolvedValue as string | undefined),
            formulaType: value === "disabled" ? formulaType : value,
        });
    };

    const handleSave = () => {
        handleOnSaveSideEffects();

        // When disabled is selected, isEnabled = false, otherwise true
        const isEnabledValue = addOnValue !== "disabled";
        const savedPayoutFormula = resolvedValue as string | undefined;
        // Keep original formulaType when disabled
        const savedFormulaType = addOnValue === "disabled" ? formulaType : addOnValue;

        // Mirrors buildCommissionUpdateRequest's own "anything to persist?" check
        // (utils/commission/updateCommission.ts) — with an empty formula (e.g. just crossed from
        // "formula" into "percent"/"fix" and nothing typed yet), that request omits
        // payout_formula/formula_type entirely, so the server keeps the old formula. "disabled" is
        // the one case that always persists regardless, since is_enabled is sent unconditionally.
        const willPersistFormula = addOnValue === "disabled" || (!!savedFormulaType && !!savedPayoutFormula);

        // Reflect the just-saved value in the collapsed cell immediately — don't wait for the
        // refetch to confirm it, same optimistic intent as handleOnSaveSideEffects above. Uses the
        // actual selected addOnValue (not savedFormulaType) so picking "disabled" shows 🚫 rather
        // than the preserved prior formula type — that substitution is only for the onSave payload.
        // Skipped entirely when the save won't actually persist anything new (see above), so the
        // collapsed cell keeps showing the last value that's genuinely saved.
        if (willPersistFormula) {
            setSavedComposedValue({ payoutFormula: savedPayoutFormula, addOnValue });
        }

        onSave?.({
            payoutFormula: savedPayoutFormula,
            formulaType: savedFormulaType,
            isEnabled: isEnabledValue,
        });
    };

    const handleMouse = (e: MouseEvent) => e.stopPropagation();

    const handleClick = (e: MouseEvent) => e.stopPropagation();

    const globalCurrency = useRecoilValue(tenantAtom).currency;

    const { loading, get } = useCurrencies();
    let currency = globalCurrency;

    if (currencyId) {
        const offerCurrency = get(currencyId);

        if (offerCurrency) {
            currency = offerCurrency;
        }
    }

    let displayValue: string | number | undefined = savedComposedValue.payoutFormula;
    if (savedComposedValue.addOnValue === "disabled") {
        displayValue = "🚫";
    } else if (savedComposedValue.addOnValue === "percent") {
        displayValue = `${savedComposedValue.payoutFormula}%`;
    } else if (savedComposedValue.addOnValue === "fix") {
        displayValue = formatCurrency(
            Number(savedComposedValue.payoutFormula),
            currency?.currency_code as CurrencyCode
        );
    }

    return (
        <PayoutFormulaWrapper
            displayValue={displayValue}
            ref={fieldRef}
            isError={!!error}
            errorMessage={error?.message as string}
        >
            <WithSaveButton onSave={handleSave} className="border-none !p-0">
                {isFormControlled ? (
                    <FormItem {...(formControl as NonNullable<typeof formControl>)} noStyle>
                        <Input
                            {...restProps}
                            className={classNames(className, "!w-full")}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                            onMouseDown={handleMouse}
                            onMouseUp={handleMouse}
                            onClick={handleClick}
                            addonAfter={
                                <AddonAfter
                                    formControl={addOnProps?.formControl}
                                    isLoading={loading}
                                    currencyLabel={formatCurrencyLabel(currency?.currency_code)}
                                />
                            }
                        />
                    </FormItem>
                ) : addOnValue === "formula" ? (
                    <div
                        className="flex items-stretch w-full"
                        onMouseDown={handleMouse}
                        onMouseUp={handleMouse}
                        onClick={handleClick}
                    >
                        <div className="flex-1 min-w-0">
                            <TagTextInput
                                value={resolvedValue as string}
                                onChange={(v) => handleChange({ payoutFormula: v, formulaType: addOnValue })}
                                categories={formulaCategories}
                                mode="free"
                                placeholder={PAYOUT_FORMULA_PLACEHOLDER}
                                className={classNames(className, "w-full rounded-r-none")}
                                disabled={restProps.disabled}
                            />
                        </div>
                        <AddonAfter
                            value={addOnValue}
                            onChange={handleAddOnChange}
                            isLoading={loading}
                            currencyLabel={formatCurrencyLabel(currency?.currency_code)}
                        />
                    </div>
                ) : (
                    <Input
                        {...restProps}
                        defaultValue={payoutFormula}
                        onChange={(e) =>
                            handleChange({
                                payoutFormula: e.target.value,
                                formulaType: addOnValue === "disabled" ? formulaType : addOnValue,
                            })
                        }
                        className={classNames(className, "!w-full")}
                        value={resolvedValue as InputProps["value"]}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        onMouseDown={handleMouse}
                        onMouseUp={handleMouse}
                        onClick={handleClick}
                        addonAfter={
                            <AddonAfter
                                value={addOnValue}
                                onChange={handleAddOnChange}
                                isLoading={loading}
                                currencyLabel={formatCurrencyLabel(currency?.currency_code)}
                            />
                        }
                    />
                )}
            </WithSaveButton>
        </PayoutFormulaWrapper>
    );
};

const AddonAfter = <T extends FieldValues = FieldValues>({
    onChange,
    value,
    currencyLabel,
    isLoading,
    ...rest
}: AddonProps<T> & {
    onChange?: (value: SelectOptionValue) => void;
    currencyLabel?: string;
    isLoading?: boolean;
}) => {
    const { formControl } = rest;

    const { setControlledValue, resolvedValue, isFormControlled } = useInteractiveCell({
        formControl,
        value,
    });

    const handleChange = (value: SelectOptionValue) => {
        setControlledValue(value);
        onChange?.(value);
    };

    const options = [
        ...SELECT_OPTIONS,
        ...(currencyLabel ? [{ label: currencyLabel, value: "fix" }] : []),
        DISABLED_OPTION,
    ];

    if (isFormControlled) {
        return (
            <FormItem {...(formControl as NonNullable<typeof formControl>)} noStyle>
                <Select
                    loading={isLoading}
                    options={options.map((option) => option)}
                    onClick={(e) => e?.stopPropagation()}
                    popupMatchSelectWidth={false}
                />
            </FormItem>
        );
    }

    return (
        <Select
            value={resolvedValue}
            loading={isLoading}
            onChange={(value) => handleChange(value as SelectOptionValue)}
            onClick={(e) => e?.stopPropagation()}
            options={options.map((option) => option)}
            popupMatchSelectWidth={false}
        />
    );
};
